import React, { useState, useEffect } from 'react';
import {
  X,
  Search,
  Plus,
  ShoppingCart,
  Package,
  DollarSign,
  Clock,
  Filter,
  CheckCircle,
  AlertCircle,
  Edit,
  Trash2
} from 'lucide-react';
import { useCartStore } from '../../store/useCartStore';
import { apiClient } from '../../lib/api';
import { Product, ProductWithPrescription } from '../../types/domain';
import { Pagination } from '../common/Pagination';
import { useEscapeKey } from '../../hooks/useKeyboardShortcuts';
import { LifecycleBadge } from '../products/LifecycleBadge';
import { useServerList } from '../../hooks/useServerList';
import { normalizePagination } from '../../lib/pagination';

const PAGE_SIZE = 20;

interface ProductCatalogModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientName?: string;
  onProductSelect?: (product: Product) => void;
  onContinue?: () => void;
  prescriptions?: any[];
  showContinueButton?: boolean;
  existingPrescriptions?: ProductWithPrescription[];
  onEditPrescription?: (productId: string) => void;
  onRemovePrescription?: (productId: string) => void;
}

export const ProductCatalogModal: React.FC<ProductCatalogModalProps> = ({
  isOpen,
  onClose,
  patientName,
  onProductSelect,
  prescriptions: _prescriptions = [],
  showContinueButton = false,
  existingPrescriptions = [],
  onEditPrescription,
  onRemovePrescription,
  onContinue
}) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [addingProduct, setAddingProduct] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<string | null>(null);
  // Every category, not just those on the page in view (that hid categories
  // whose products sat on later pages).
  const [allCategories, setAllCategories] = useState<string[]>([]);

  const { items, addItem } = useCartStore();

  // ESC key to close modal
  useEscapeKey(isOpen, onClose);

  // Search and category are both applied by the server, over the whole
  // catalog; the page is never re-filtered here (that hid matches the server
  // found, e.g. in the description, and categories on other pages).
  const list = useServerList<Product, { categories: string }>({
    fetchPage: async ({ page, pageSize, search, filters, signal }) => {
      const params = new URLSearchParams({ includePricing: 'true', page: String(page), limit: String(pageSize) });
      if (search) params.set('search', search);
      if (filters.categories) params.set('category', filters.categories);
      // Physicians need catalog-enforced, fully inherited pricing
      const response = await apiClient.get<any>(`/products?${params}`, { signal });
      if (!response?.success || !response.data?.products) throw new Error('Failed to load products');
      const products: Product[] = response.data.products;
      const p = normalizePagination(response.data.pagination, { page, pageSize, itemCount: products.length });
      return { items: products, total: p.total, hasMore: p.hasMore };
    },
    filters: { categories: selectedCategories.join(',') },
    pageSize: PAGE_SIZE,
    debounceMs: 400,
    enabled: isOpen,
  });
  const products = list.items;
  const loading = list.loading;
  const error = list.error;
  const total = list.total ?? products.length;
  const { setPage } = list;

  // Reopening the modal starts from the first page.
  useEffect(() => { if (isOpen) setPage(1); }, [isOpen, setPage]);

  useEffect(() => {
    if (!isOpen || allCategories.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<any>('/products/meta/categories');
        if (!cancelled) setAllCategories(res?.data?.categories || []);
      } catch (err) {
        console.error('Error fetching product categories:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, allCategories.length]);

  const handleAddToCart = async (product: Product, quantity: number = 1) => {
    if (onProductSelect) {
      // For prescription workflow
      setAddingProduct(product._id);
      try {
        onProductSelect(product);
        setJustAdded(product._id);
        setTimeout(() => setJustAdded(null), 3000);
      } finally {
        setAddingProduct(null);
      }
    } else {
      // For regular cart
      setAddingProduct(product._id);
      try {
        addItem({
          id: product._id,
          name: product.displayName,
          price: product.calculatedPrice?.finalPrice || product.basePrice,
          selectedVariants: {},
          isPeptide: (product as any).isPeptide
        }, quantity);
        setJustAdded(product._id);
        setTimeout(() => setJustAdded(null), 3000);
      } finally {
        setAddingProduct(null);
      }
    }
  };

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const getProductStatus = (productId: string) => {
    if (onProductSelect) {
      // Check if product has prescription
      return existingPrescriptions.some(p => p.product._id === productId);
    } else {
      // Check if product is in cart
      return items.some(item => item.productId === productId);
    }
  };

  const getProductQuantity = (productId: string) => {
    if (onProductSelect) {
      const prescription = existingPrescriptions.find(p => p.product._id === productId);
      return prescription?.prescription.quantity || 0;
    } else {
      const cartItem = items.find(item => item.productId === productId);
      return cartItem?.quantity || 0;
    }
  };

  const totalItems = onProductSelect ? existingPrescriptions.length : items.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = onProductSelect ? 0 : items.reduce((sum, item) => {
    return sum + item.totalPrice;
  }, 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Product Catalog</h2>
            {patientName && (
              <p className="text-gray-600">Creating order for {patientName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-grow overflow-y-auto">
        {/* Cart/Prescription Summary */}
        {(totalItems > 0 || existingPrescriptions.length > 0) && (
            <div className="sticky top-0 bg-blue-50 border-b border-blue-200 p-4 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <ShoppingCart className="h-5 w-5 text-blue-600" />
                <span className="text-blue-800 font-medium">
                  {onProductSelect ? 
                    `${existingPrescriptions.length} prescription${existingPrescriptions.length !== 1 ? 's' : ''} added` :
                    `${totalItems} item${totalItems !== 1 ? 's' : ''} in cart`
                  }
                </span>
              </div>
              {!onProductSelect && (
                <div className="text-blue-800 font-bold">
                  Total: ${totalAmount.toFixed(2)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={list.search}
                  onChange={(e) => list.setSearch(e.target.value)}
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
                </div>
              </div>
              <div className="relative">
                <button className="flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-50 w-full md:w-auto">
                  <Filter className="w-5 h-5" />
                  <span>Filters</span>
                  <span className="bg-gray-200 text-gray-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                    {selectedCategories.length}
                  </span>
                </button>
                {/* Dropdown would go here, simplified for brevity */}
              </div>
              </div>
            </div>
            
          {/* Category Pills */}
          <div className="px-6 pb-4 border-b border-gray-200">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategories([])}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedCategories.length === 0
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {allCategories.map(category => (
                <button
                  key={category}
                  onClick={() => handleCategoryToggle(category)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    selectedCategories.includes(category)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>


          {/* Product List */}
          <div className="p-6">
          {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
                    <div className="h-24 bg-gray-200 rounded-md"></div>
                    <div className="mt-4 h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="mt-2 h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="mt-4 h-10 bg-gray-200 rounded-lg"></div>
              </div>
                ))}
            </div>
          ) : error ? (
              <div className="text-center py-10">
                <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
                <p className="mt-1 text-sm text-gray-500">{error}</p>
                <button
                  onClick={list.reload}
                  className="mt-6 bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700"
                >
                  Try Again
                </button>
            </div>
          ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map(product => {
                  const isAdded = getProductStatus(product._id);
                    const quantity = getProductQuantity(product._id);
                    const isOOS = (product as any).outOfStock === true;
                    const isLowStock = (product as any).lowStockWarning === true;
                    return (
                    <div
                      key={product._id}
                      className={`bg-white rounded-lg border-2 transition-all ${isOOS ? 'border-red-300 opacity-90' : isAdded ? 'border-green-500' : 'border-gray-200 hover:border-blue-500'} `}
                    >
                      <div className="p-4">
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex flex-col gap-1">
                            <h3 className="font-bold text-gray-800 text-lg">{product.displayName}</h3>
                            <div className="flex items-center gap-2 flex-wrap">
                              {isOOS && (
                                <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-0.5 rounded">
                                  Out of Stock
                                </span>
                              )}
                              {isLowStock && !isOOS && (
                                <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded">
                                  Low Stock
                                </span>
                              )}
                              {!isOOS && <LifecycleBadge status={(product as any).lifecycleStatus} audience="clinic" />}
                            </div>
                          </div>
                          {isAdded && onProductSelect && (
                            <span className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" /> Added
                              </span>
                            )}
                          </div>
                        <p className="text-sm text-gray-500 mt-1">{product.format}, {product.size}</p>
                        {isLowStock && !isOOS && (
                          <p className="text-xs text-amber-700 mt-2">
                            Low stock, please confirm availability for on time fulfillment
                          </p>
                        )}
                      </div>

                      <div className="p-4 bg-gray-50 border-t border-b border-gray-200">
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 flex items-center gap-2"><Package className="w-4 h-4" />Categories</span>
                            <span className="font-medium text-gray-900">{product.categories.join(', ')}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 flex items-center gap-2"><DollarSign className="w-4 h-4" />Price</span>
                            <span className="font-bold text-lg text-gray-900">
                              ${(product.calculatedPrice?.finalPrice || product.basePrice).toFixed(2)}
                            </span>
                          </div>
                          {product.allowPerUnitOrdering && product.perUnitPrice && (
                             <div className="flex items-center justify-between text-xs text-gray-500">
                              <span></span>
                              <span>(${product.perUnitPrice.toFixed(2)} / unit)</span>
                            </div>
                          )}
                           <div className="flex items-center justify-between">
                            <span className="text-gray-600 flex items-center gap-2"><Clock className="w-4 h-4" />Est. Ship</span>
                            <span className="font-medium text-gray-900">{product.estimatedShipDays} days</span>
                          </div>
                        </div>
                      </div>

                      <div className="p-4">
                        {isAdded ? (
                          <div className="flex items-center justify-between gap-2">
                            {onEditPrescription && (
                                <button
                                  onClick={() => onEditPrescription(product._id)}
                                className="w-full text-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center gap-2"
                                >
                                <Edit className="w-4 h-4" /> Edit
                                </button>
                            )}
                            {onRemovePrescription && (
                                <button
                                onClick={() => onRemovePrescription(product._id)}
                                className="w-full text-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center gap-2"
                                >
                                <Trash2 className="w-4 h-4" /> Remove
                                </button>
                            )}
                            {!onProductSelect && (
                              <div className="flex items-center justify-center w-full">
                                <span className="text-lg font-bold text-green-600">{quantity} in cart</span>
                              </div>
                            )}
                          </div>
                        ) : isOOS ? (
                            <button
                              disabled
                              className="w-full text-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-400 bg-gray-100 cursor-not-allowed flex items-center justify-center gap-2"
                            >
                              Out of Stock
                            </button>
                        ) : (
                            <button
                              onClick={() => handleAddToCart(product)}
                            disabled={addingProduct === product._id}
                            className={`w-full text-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white flex items-center justify-center gap-2 transition-all ${
                              justAdded === product._id
                                ? 'bg-green-500'
                                : addingProduct === product._id
                                  ? 'bg-blue-400 cursor-not-allowed'
                                  : 'bg-blue-600 hover:bg-blue-700'
                            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                          >
                            {justAdded === product._id ? (
                                <>
                                <CheckCircle className="w-5 h-5" /> Added!
                              </>
                            ) : addingProduct === product._id ? (
                              'Adding...'
                              ) : (
                                <>
                                <Plus className="w-5 h-5" /> Add to Order
                                </>
                              )}
                            </button>
                        )}
                      </div>
                          </div>
                    );
                  })}
            </div>
          )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 border-t border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Showing {products.length} of {total} products
            </p>
            {showContinueButton && existingPrescriptions.length > 0 && (
              <button
                onClick={onContinue}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 flex items-center gap-2"
              >
                <span>Continue to Review</span>
                <div className="bg-blue-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                  {existingPrescriptions.length}
                </div>
              </button>
            )}
          </div>
          
          {/* Pagination */}
          {list.totalPages !== null && list.totalPages > 1 && (
            <Pagination
              currentPage={list.page}
              totalPages={list.totalPages}
              onPageChange={list.setPage}
              totalItems={total}
              itemsPerPage={PAGE_SIZE}
            />
          )}
        </div>
      </div>
    </div>
  );
}; 